import { Filter, Filters, SortOrder } from "@/types/order-table";
import {
  createContext,
  FC,
  useState,
  useContext,
  Dispatch,
  SetStateAction,
  PropsWithChildren,
  ReactNode,
  useMemo,
  useCallback,
  useEffect,
} from "react";
import { useLocation } from "react-router-dom";

const noop = () => null;

const defaultValues = {
  sortBy: "",
  sortOrder: null,
  filters: {
    authStatus: ["Not Started"],
    departments: [],
    providerNames: [],
    orderTypes: [],
    insurances: [],
    patientNames: [],
    priority: [],
    assignedUser: [],
    clinicalFacilityDetails: [],
    secondaryInsurances: [],
    performDateRangeStar: [""],
    performDateRangeEn: [""],
  },
  pageNumber: 1,
  updateFilters: noop,
  updateMultipleFilters: noop,
  setPageNumber: noop,
  setSortOrder: noop,
  setSortBy: noop,
};

export interface Orders {
  sortBy?: string;
  sortOrder?: SortOrder;
  filters: Filters;
  pageNumber: number;
  updateFilters: (type: Filter, filters: string[]) => void;
  updateMultipleFilters: (partial: Partial<Filters>) => void;
  setPageNumber: Dispatch<SetStateAction<number>>;
  setSortOrder: Dispatch<SetStateAction<SortOrder>>;
  setSortBy: Dispatch<SetStateAction<string>>;
}

const OrdersContext = createContext<Orders>(defaultValues);

export const OrdersContextProvider: FC<PropsWithChildren<ReactNode>> = ({
  children,
}) => {
  const [sortOrder, setSortOrder] = useState<SortOrder>(
    defaultValues.sortOrder,
  );
  const [sortBy, setSortBy] = useState<string>(defaultValues.sortBy);
  const [filters, setFilters] = useState<Filters>(defaultValues.filters);
  const [pageNumber, setPageNumber] = useState(defaultValues.pageNumber);
  const location = useLocation();

  const updateFilters = useCallback(
    (filterType: Filter, filterValues: string[]) => {
      setFilters({
        ...filters,
        [filterType]: filterValues,
      });
      setPageNumber(1);
    },
    [filters],
  );

  const updateMultipleFilters = useCallback(
    (changes: Partial<Filters>, values?: string[]) => {
      values ?
        setFilters({ ...filters, [String(changes)]: values }) :
        setFilters({ ...filters, ...changes });
        setPageNumber(1);
      }, [filters]);

  const values = useMemo(
    () => ({
      sortBy,
      sortOrder,
      filters,
      pageNumber,
      updateFilters,
      updateMultipleFilters,
      setPageNumber,
      setSortOrder,
      setSortBy,
    }),
    [
      sortBy,
      sortOrder,
      filters,
      pageNumber,
      updateMultipleFilters,
      updateFilters,
      setPageNumber,
      setSortOrder,
      setSortBy,
    ],
  );

  useEffect(() => {
    setPageNumber(1);
    updateFilters("assignedUser", []);
    // eslint-disable-next-line
  }, [location]);

  return (
    <OrdersContext.Provider value={values}>{children}</OrdersContext.Provider>
  );
};

export const useOrdersContext = () => useContext(OrdersContext);
